/**
 * WebCodecs + Mediabunny ベースの動画生成エンジン
 *
 * ffmpeg.wasmの代替として、ブラウザネイティブのWebCodecs APIとMediabunnyを使用して
 * GPU活用による高速な動画生成を実現する。
 *
 * Worker内で処理を行うため、複数タブでの同時実行が可能。
 */
import type { VideoSettings, SubtitleSettings } from '@/types';
import type { VideoProgressCallback, SceneWithBlobData } from '@/types/application';
import { getTransitionSoundFile } from '@/lib/constants';
import { logger } from '@/lib/logger';
import { VideoGenerationError } from '@/application/errors/VideoGenerationError';
import type {
  WebCodecsWorkerInputMessage,
  WebCodecsWorkerOutputMessage,
} from '@/workers/webcodecs-video.worker';

export interface WebCodecsVideoGeneratorOptions {
  scenes: SceneWithBlobData[];
  videoSettings: VideoSettings;
  subtitleSettings?: SubtitleSettings;
  globalSubtitleEnabled?: boolean;
  onProgress?: VideoProgressCallback;
  onWorkerCreated?: (worker: Worker) => void;
}

export interface WebCodecsVideoGeneratorResult {
  videoBlob: Blob;
  warnings: string[];
}

/**
 * WebCodecs + Mediabunny を使用した動画生成（Worker版）
 *
 * 処理はWeb Worker内で実行されるため、メインスレッドをブロックせず、
 * 複数タブでの同時実行が可能。
 */
export async function generateVideoWithWebCodecs(
  options: WebCodecsVideoGeneratorOptions
): Promise<WebCodecsVideoGeneratorResult> {
  const {
    scenes,
    videoSettings,
    subtitleSettings,
    globalSubtitleEnabled = false,
    onProgress,
    onWorkerCreated,
  } = options;

  const warnings: string[] = [];

  logger.info('WebCodecs video generation started (Worker mode)', {
    sceneCount: scenes.length,
    enableTransition: videoSettings.enableTransition,
  });

  onProgress?.('Initializing WebCodecs Worker...', 0, undefined, 0, scenes.length);

  // トランジション音をメインスレッドで読み込む（Worker内ではfetch制限があるため）
  let transitionSoundData: ArrayBuffer | undefined;
  if (videoSettings.enableTransition && videoSettings.transitionSound !== 'none') {
    const soundFile = getTransitionSoundFile(videoSettings.transitionSound);
    if (soundFile) {
      try {
        const response = await fetch(soundFile);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        transitionSoundData = await response.arrayBuffer();
        logger.debug('Transition sound loaded for Worker', { soundFile });
      } catch (error) {
        const warningMsg = `トランジション音「${videoSettings.transitionSound}」の読み込みに失敗しました`;
        logger.warn(warningMsg, { soundFile, error });
        warnings.push(warningMsg);
      }
    }
  }

  return new Promise((resolve, reject) => {
    const worker = new Worker(
      new URL('@/workers/webcodecs-video.worker.ts', import.meta.url),
      { type: 'module' }
    );

    // キャンセル用にWorker参照を渡す
    onWorkerCreated?.(worker);

    worker.onmessage = (event: MessageEvent<WebCodecsWorkerOutputMessage>) => {
      const { type } = event.data;

      if (type === 'progress') {
        const { message, progress, warning, currentScene, totalScenes } = event.data;
        onProgress?.(message, progress, warning, currentScene, totalScenes);
      } else if (type === 'complete') {
        const { videoData } = event.data;
        const videoBlob = new Blob([videoData], { type: 'video/mp4' });

        logger.info('WebCodecs video generation completed (Worker mode)', {
          fileSize: videoBlob.size,
        });

        worker.terminate();
        resolve({ videoBlob, warnings });
      } else if (type === 'error') {
        const { error, code } = event.data;
        logger.error('WebCodecs Worker error', new Error(error), { code });
        worker.terminate();
        // 専用の文言を持つ失敗だけ型付きエラーに載せ替え、それ以外は汎用のまま返す
        reject(code ? new VideoGenerationError(code, error) : new Error(error));
      }
    };

    worker.onerror = (error) => {
      logger.error('WebCodecs Worker fatal error', new Error(error.message));
      worker.terminate();
      reject(new Error(`Worker error: ${error.message}`));
    };

    // Transferableとして送信するArrayBufferを収集
    const transferables: Transferable[] = [];
    const scenesToSend = scenes.map((scene) => {
      // ArrayBufferをcloneして送信（元のデータを保持するため）
      const imageData = scene.imageData.slice(0);
      transferables.push(imageData);

      let audioData: ArrayBuffer | null = null;
      if (scene.audioData) {
        audioData = scene.audioData.slice(0);
        transferables.push(audioData);
      }

      return {
        ...scene,
        imageData,
        audioData,
      };
    });

    if (transitionSoundData) {
      transferables.push(transitionSoundData);
    }

    const message: WebCodecsWorkerInputMessage = {
      type: 'generate',
      scenes: scenesToSend,
      videoSettings,
      subtitleSettings,
      globalSubtitleEnabled,
      transitionSoundData,
    };

    logger.debug('Sending message to WebCodecs Worker', {
      sceneCount: scenes.length,
      transferablesCount: transferables.length,
    });

    worker.postMessage(message, transferables);
  });
}
